'use client';

import { Box } from '@mui/material';
import { usePathname, redirect } from 'next/navigation';
import { type PropsWithChildren } from 'react';

import { Loader } from '@/components/shared/loader';
import { useFeatureFlags, useLaunchDarkly } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import { useUser } from '@/contexts/useUser';
import { getDocsUrl } from '@/docs/docsUrls';

export function AuthChecker({ children }: PropsWithChildren) {
  const { user, currentProjectId } = useUser();
  const {
    SkipOnboarding,
    BypassSourceAccountRequirement,
    SaasInventoryDefaultLanding,
  } = useFeatureFlags();
  const pathname = usePathname();
  const { isReady } = useLaunchDarkly();
  const { sourceAccounts } = useSourceAccounts();
  const { isGlobalRole, roles, permissions } = useRoles();

  const isApplicationReady =
    !!user &&
    !!currentProjectId &&
    !!sourceAccounts &&
    isReady &&
    roles.length > 0 &&
    Object.keys(permissions).length > 0;

  if (!isApplicationReady) {
    return <LoaderComponent />;
  }

  if (
    user.eonAccount?.domain === 'eon-docs' &&
    !isGlobalRole(user.selectedRole)
  ) {
    redirect(getDocsUrl('root'));
  }

  if (
    sourceAccounts?.length ||
    SkipOnboarding ||
    BypassSourceAccountRequirement ||
    pathname.startsWith('/onboarding') ||
    pathname.startsWith('/global-management')
  ) {
    // Redirect above VerticalLayout so the nav shell never flashes before the
    // flag-gated landing resolves.
    if (pathname === '/') {
      redirect(SaasInventoryDefaultLanding ? '/saas-inventory' : '/inventory');
    }
    return children;
  }

  // redirect('/global-management/scanning-accounts');
  redirect('/onboarding');
}

const LoaderComponent = () => (
  <Box
    className='h-full'
    sx={{
      background: 'var(--mui-palette-background-pageGradient)',
    }}
  >
    <Loader />
  </Box>
);
